/**
 * The one way this app talks to FastAPI.
 *
 * Three things it exists to guarantee:
 *
 * 1. **Cookies always travel.** Sessions are cookie-based
 *    (`server/app/auth/sessions.py`) and the dev server is a different origin
 *    from the API, so every request is `credentials: 'include'`. A `fetch`
 *    written by hand somewhere else in the app will silently be anonymous.
 * 2. **Errors are typed, not strings.** FastAPI has three different `detail`
 *    shapes and one of them is a list. `parseErrorBody` collapses all three
 *    into one {@link ApiError} carrying a stable `code`, a human `message` and,
 *    for a 422, the per-field issues.
 * 3. **A non-JSON failure is still an ApiError.** A 502 from a proxy returns
 *    HTML; a dead API returns nothing at all. Both must arrive at the caller as
 *    an `ApiError`, never as a `SyntaxError` from `response.json()`.
 */

/** Trailing slash stripped so `${API_BASE}${path}` never doubles up. */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? 'http://localhost:8000').replace(
  /\/+$/,
  '',
)

/** One entry of FastAPI's 422 body. */
export interface FieldIssue {
  /** Dotted path into the request body, e.g. `body.email` → `email`. */
  field: string
  message: string
  type?: string
}

interface ParsedError {
  code: string
  message: string
  issues: FieldIssue[]
}

/** FastAPI's `RequestValidationError` entries. */
interface RawValidationIssue {
  loc?: unknown[]
  msg?: string
  type?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Collapse whatever came back into `{code, message, issues}`.
 *
 * Handles, in order: `{detail: [...]}` (422 validation), `{detail: {code,
 * message}}` (the shape every hand-raised HTTPException in this codebase
 * uses), `{detail: "..."}` (FastAPI's default), and anything else.
 */
export function parseErrorBody(status: number, body: unknown): ParsedError {
  const fallback = `Request failed with status ${status}.`

  if (!isRecord(body)) {
    return {
      code: `http_${status}`,
      message: typeof body === 'string' && body.trim() ? body.trim() : fallback,
      issues: [],
    }
  }

  const detail = body.detail

  if (Array.isArray(detail)) {
    const issues: FieldIssue[] = detail.map((entry) => {
      const raw = (isRecord(entry) ? entry : {}) as RawValidationIssue
      // `loc` is ('body', 'email') or ('query', 'role'); the first element is
      // the request part, which is noise to a form.
      const path = Array.isArray(raw.loc) ? raw.loc.slice(1).map(String) : []
      return {
        field: path.join('.') || 'request',
        message: typeof raw.msg === 'string' ? raw.msg : 'Invalid value.',
        ...(typeof raw.type === 'string' ? { type: raw.type } : {}),
      }
    })
    const first = issues[0]
    return {
      code: 'validation_error',
      message: first ? `${first.field}: ${first.message}` : 'The request was not valid.',
      issues,
    }
  }

  if (isRecord(detail)) {
    const code = typeof detail.code === 'string' ? detail.code : `http_${status}`
    const message = typeof detail.message === 'string' ? detail.message : fallback
    return { code, message, issues: [] }
  }

  if (typeof detail === 'string' && detail.trim()) {
    return { code: `http_${status}`, message: detail.trim(), issues: [] }
  }

  // Health checks answer `{status, database, detail}` with a 503 and no
  // `detail.message`; fall back to any string field that reads like one.
  const message = typeof body.message === 'string' ? body.message : fallback
  return { code: `http_${status}`, message, issues: [] }
}

/**
 * Anything the API refused, plus anything that stopped it being asked.
 *
 * `status` is 0 for a transport failure — that is the one case where the
 * server never saw the request, and the sign-in screen says so differently
 * from a rejected code.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly issues: FieldIssue[]
  readonly body: unknown
  readonly method: string
  readonly url: string

  constructor(init: {
    status: number
    code: string
    message: string
    issues?: FieldIssue[]
    body?: unknown
    method: string
    url: string
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.issues = init.issues ?? []
    this.body = init.body
    this.method = init.method
    this.url = init.url
    // Restores `instanceof` under the ES5 downlevel some toolchains still emit.
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  /** The request never reached the API (offline, DNS, CORS preflight refused). */
  get isNetworkError(): boolean {
    return this.status === 0
  }

  /** No session, or an expired one. The shell answers this by showing SignIn. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Signed in, but this role may not. Never a reason to sign the user out. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** 422 — the body was malformed; {@link issues} names the fields. */
  get isValidationError(): boolean {
    return this.status === 422
  }

  /**
   * 501 — a real answer, not a bug. `POST /api/matters` refuses any
   * `proceeding_type` other than `EXAM_OA` this way (plan §5 P0).
   */
  get isNotImplemented(): boolean {
    return this.status === 501
  }

  /** The message for one field, if the server blamed it. */
  issueFor(field: string): string | undefined {
    return this.issues.find((issue) => issue.field === field)?.message
  }
}

/** Type guard, for `catch (err)` blocks where `err` is `unknown`. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** Serialised as JSON unless it is already a `FormData`/`Blob`/string. */
  body?: unknown
  /** Appended as a query string; `null`/`undefined` entries are dropped. */
  query?: Record<string, QueryValue>
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`
  if (!query) return base
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base
}

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer
  )
}

/**
 * Issue one request.
 *
 * Returns `undefined` for 204/205 — `POST /api/auth/logout` answers 204 and a
 * caller typed `Promise<void>` should not have to think about it.
 */
export async function apiFetch<T = unknown>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, query, headers, ...rest } = options
  const url = buildUrl(path, query)

  const requestHeaders = new Headers(headers)
  if (!requestHeaders.has('Accept')) requestHeaders.set('Accept', 'application/json')

  let payload: BodyInit | undefined
  if (body !== undefined && body !== null) {
    if (isRawBody(body)) {
      payload = body
    } else {
      payload = JSON.stringify(body)
      if (!requestHeaders.has('Content-Type')) {
        requestHeaders.set('Content-Type', 'application/json')
      }
    }
  }

  let response: Response
  try {
    response = await fetch(url, {
      ...rest,
      method,
      headers: requestHeaders,
      // Non-negotiable: the session lives in a cookie on another origin.
      credentials: 'include',
      ...(payload === undefined ? {} : { body: payload }),
    })
  } catch (cause) {
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message:
        'Could not reach the API. Check that the server is running and that VITE_API_BASE points at it.',
      body: cause,
      method,
      url,
    })
  }

  if (response.status === 204 || response.status === 205) {
    if (!response.ok) {
      throw new ApiError({
        status: response.status,
        code: `http_${response.status}`,
        message: response.statusText || 'Request failed.',
        method,
        url,
      })
    }
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  let parsed: unknown
  if (contentType.includes('json')) {
    try {
      parsed = await response.json()
    } catch {
      parsed = undefined
    }
  } else {
    parsed = await response.text().catch(() => '')
  }

  if (!response.ok) {
    const { code, message, issues } = parseErrorBody(response.status, parsed)
    throw new ApiError({
      status: response.status,
      code,
      message,
      issues,
      body: parsed,
      method,
      url,
    })
  }

  return parsed as T
}

/** Verb helpers. `api.get<Matter[]>('/api/matters')` is the intended shape. */
export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiFetch<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => apiFetch<T>('DELETE', path, options),
} as const

/**
 * The message to put in front of a user.
 *
 * Everything thrown by this module is an `ApiError` with a written message;
 * this only exists for the `catch (err: unknown)` boundary, where a
 * programming error could also arrive.
 */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong.'
}
